/**
 * Compound **Table** API: `Provider` + layout primitives + optional sorting + pagination.
 *
 * **When to use what**
 * - `Table.Provider` — required parent; pass `data` and optionally `sortFns`, `sort`, `onSortChange`.
 * - `Table.Scroll` / `Table.Table` / `Table.Th` / `Table.Td` — desktop table shell (forward refs).
 * - `Table.SortableHead` — only if that column exists in `sortFns` (no-op click otherwise for unknown ids).
 * - `Table.Pagination` / `TablePagination` — `mode="local"` (stateful page index) or `mode="links"` (anchors).
 *
 * **Responsive pattern** — `useTableContext().isDesktop` mirrors the `md` breakpoint; render cards when `false`.
 *
 * @example Sorted table with local pagination
 * ```tsx
 * <Table.Provider data={rows} sortFns={sortFns}>
 *   <Table.Scroll>
 *     <Table.Table>
 *       <thead>...</thead>
 *       <tbody>...</tbody>
 *     </Table.Table>
 *   </Table.Scroll>
 *   <Table.Pagination
 *     mode="local"
 *     page={page}
 *     pageSize={10}
 *     totalItems={total}
 *     onPageChange={setPage}
 *   />
 * </Table.Provider>
 * ```
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { Pagination } from 'gatorcomponents';

import { locale } from '@/utils/locale';
import {
  BodyCell,
  DataTable,
  HeaderCell,
  PaginationNav,
  SortHeaderButton,
  TableScroll,
  VisuallyHidden,
} from './Table.styles';
import {
  TABLE_ARIA_LABELS,
  TABLE_CONTEXT_ERROR,
  TABLE_FIRST_PAGE_ONE_BASED,
  TABLE_PAGINATION_NAV_FALLBACK,
  TABLE_SORT_DIRECTION,
  TABLE_ZERO_TO_ONE_BASED_OFFSET,
} from './table.constants';
import {
  clampPaginationPage,
  resolveAriaSort,
  sortTableData,
  totalPagesLocal,
  useIsMdUp,
} from './table.utils';
import type {
  TableContextValue,
  TablePaginationLinkItem,
  TablePaginationNavLink,
  TablePaginationProps,
  TableProviderProps,
  TableSortableHeadProps,
  TableSortFns,
  TableSortState,
} from './Table.types';

const TableContext = createContext<TableContextValue<unknown> | null>(null);

/**
 * Supplies `sortedData`, sort helpers, and `isDesktop` to child components.
 *
 * @typeParam T - Row type of the `data` prop (same as `Table.Provider<T>` usage).
 */
function TableProvider<T>({
  data,
  sortFns: sortFnsProp,
  children,
  sort: sortProp,
  onSortChange,
  'data-testid': dataTestId,
}: TableProviderProps<T>) {
  const sortFns = sortFnsProp ?? ({} as TableSortFns<T>);
  const isDesktop = useIsMdUp();
  const isControlled = typeof onSortChange === 'function';
  const [uncontrolledSort, setUncontrolledSort] = useState<TableSortState | null>(null);
  const sortState = isControlled ? sortProp || null : uncontrolledSort;

  const sortedData = useMemo(
    () => sortTableData(data, sortState, sortFns),
    [data, sortFns, sortState],
  );

  const applySort = useCallback(
    (next: TableSortState | null) => {
      if (isControlled) {
        if (onSortChange) {
          onSortChange(next);
        }
      } else {
        setUncontrolledSort(next);
      }
    },
    [isControlled, onSortChange],
  );

  const toggleSort = useCallback(
    (columnId: string) => {
      if (!sortFns[columnId]) {
        return;
      }
      if (sortState && sortState.columnId === columnId) {
        if (sortState.direction === TABLE_SORT_DIRECTION.asc) {
          applySort({ columnId, direction: TABLE_SORT_DIRECTION.desc });
        } else {
          applySort({ columnId, direction: TABLE_SORT_DIRECTION.asc });
        }
        return;
      }
      applySort({ columnId, direction: TABLE_SORT_DIRECTION.asc });
    },
    [applySort, sortFns, sortState],
  );

  const getAriaSort = useCallback(
    (columnId: string) => resolveAriaSort(sortState, columnId),
    [sortState],
  );

  const value = useMemo<TableContextValue<T>>(
    () => ({
      data,
      sortedData,
      sortState,
      isDesktop,
      toggleSort,
      getAriaSort,
    }),
    [data, sortedData, sortState, isDesktop, toggleSort, getAriaSort],
  );

  const inner = (
    <TableContext.Provider value={value as TableContextValue<unknown>}>
      {children}
    </TableContext.Provider>
  );

  if (dataTestId) {
    return <div data-testid={dataTestId}>{inner}</div>;
  }

  return inner;
}

/**
 * Hook for children of `Table.Provider`. Prefer destructuring `{ sortedData, isDesktop }` in layouts.
 *
 * @typeParam T - Row type aligned with `Table.Provider<T>`.
 * @throws {Error} If there is no ancestor `Table.Provider` (`TABLE_CONTEXT_ERROR`).
 */
export function useTableContext<T = unknown>(): TableContextValue<T> {
  const ctx = useContext(TableContext);
  if (!ctx) {
    throw new Error(TABLE_CONTEXT_ERROR);
  }
  return ctx as TableContextValue<T>;
}

/** Overflow-x wrapper around `Table.Table`; accepts standard `div` props and `ref`. */
const TableScrollWrapper = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof TableScroll>
>((props, ref) => <TableScroll ref={ref} {...props} />);
TableScrollWrapper.displayName = 'Table.Scroll';

/** Styled native table with `role="table"`. */
const TableNative = React.forwardRef<
  HTMLTableElement,
  React.ComponentPropsWithoutRef<typeof DataTable>
>((props, ref) => <DataTable ref={ref} role="table" {...props} />);
TableNative.displayName = 'Table.Table';

/** Non-interactive column header (`<th scope="col">`). */
const Th = React.forwardRef<
  HTMLTableCellElement,
  React.ComponentPropsWithoutRef<typeof HeaderCell>
>((props, ref) => <HeaderCell ref={ref} scope="col" {...props} />);
Th.displayName = 'Table.Th';

/** Body cell (`<td>`). */
const Td = React.forwardRef<
  HTMLTableCellElement,
  React.ComponentPropsWithoutRef<typeof BodyCell>
>((props, ref) => <BodyCell ref={ref} {...props} />);
Td.displayName = 'Table.Td';

/** Sortable header: toggles sort for `columnId` and wires `aria-sort` + screen-reader hint. */
const SortableHead = ({
  columnId,
  sortInstructionColumn,
  children,
  ...rest
}: TableSortableHeadProps) => {
  const { toggleSort, getAriaSort } = useTableContext();
  const label = sortInstructionColumn ?? columnId;
  const sortInstruction = locale('sharedUi.table.sortByColumn', {
    column: label,
  });
  const ariaSort = getAriaSort(columnId);

  return (
    <HeaderCell scope="col" aria-sort={ariaSort} {...rest}>
      <SortHeaderButton type="button" onClick={() => toggleSort(columnId)}>
        <span aria-hidden="true">{children}</span>
        <VisuallyHidden>{sortInstruction}</VisuallyHidden>
      </SortHeaderButton>
    </HeaderCell>
  );
};

SortableHead.displayName = 'Table.SortableHead';

/**
 * Pagination UI. **`local`** — controlled 1-based `page` with gatorcomponents `Pagination`.
 * **`links`** — prev/next + page anchors; optional `renderPageLink` for SPA routers.
 */
const TablePaginationComponent = (props: TablePaginationProps) => {
  const { mode } = props;

  if (mode === 'local') {
    const {
      page, pageSize, totalItems, onPageChange,
    } = props;
    const totalP = totalPagesLocal(totalItems, pageSize);
    const safePage = clampPaginationPage(page, totalP);
    return (
      <PaginationNav aria-label={TABLE_ARIA_LABELS.paginationNav}>
        <Pagination
          key={safePage}
          pagesAmount={totalP}
          page={safePage - TABLE_ZERO_TO_ONE_BASED_OFFSET}
          setPage={zeroBasedPage => onPageChange(zeroBasedPage + TABLE_ZERO_TO_ONE_BASED_OFFSET)
          }
          startIn={TABLE_FIRST_PAGE_ONE_BASED}
        />
      </PaginationNav>
    );
  }

  const {
    prev, next, pages, renderPageLink,
  } = props;

  const renderPage = (item: TablePaginationLinkItem, index: number) => {
    const active = Boolean(item.active);
    const children = item.label;
    if (renderPageLink) {
      return (
        <React.Fragment key={`${item.href}-${String(index)}`}>
          {renderPageLink({ href: item.href, active, children })}
        </React.Fragment>
      );
    }
    return (
      <a
        key={`${item.href}-${String(index)}`}
        href={item.href}
        aria-current={active ? 'page' : undefined}
      >
        {children}
      </a>
    );
  };

  const renderNavAnchor = (
    nav: TablePaginationNavLink | undefined,
    fallbackLabel: string,
  ) => {
    if (!nav) {
      return null;
    }
    const label = nav.label != null ? nav.label : fallbackLabel;
    if (nav.disabled) {
      return <span aria-disabled="true">{label}</span>;
    }
    return <a href={nav.href}>{label}</a>;
  };

  return (
    <PaginationNav aria-label={TABLE_ARIA_LABELS.paginationNav}>
      {renderNavAnchor(prev, TABLE_PAGINATION_NAV_FALLBACK.previous)}
      {pages && pages.map((p, i) => renderPage(p, i))}
      {renderNavAnchor(next, TABLE_PAGINATION_NAV_FALLBACK.next)}
    </PaginationNav>
  );
};

/**
 * Namespace object so consumers get autocomplete on `Table.Provider`, `Table.Scroll`, etc.
 */
export const Table = {
  Provider: TableProvider,
  Scroll: TableScrollWrapper,
  Table: TableNative,
  Th,
  Td,
  SortableHead,
  Pagination: TablePaginationComponent,
};

/** Alias of `Table.Pagination` for tree-shaking / direct imports without the `Table` namespace. */
export { TablePaginationComponent as TablePagination };
